import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Avatar,
} from '@mui/material';
import { usePayers } from '@features/payer-search';

interface Payer {
  id: string;
  name: string;
  eligibility: string;
}

interface PayerSearchProps {
  onPayerSelect: (payer: Payer | null) => void;
  value: Payer | null;
  disabled?: boolean;
}

const PayerSearch: React.FC<PayerSearchProps> = ({ onPayerSelect, value, disabled = false }) => {
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(inputValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // Use RTK Query to fetch payers with search
  const { payers: payerResults, isLoading } = usePayers({
    search: debouncedSearch.length >= 2 ? debouncedSearch : null,
    perPage: 50,
  });

  // Map new payer format to legacy format for backwards compatibility
  const mappedPayers = useMemo(() => {
    return payerResults.map(payer => ({
      id: payer.stedi_id, // Use stedi_id as the payer_id
      name: payer.display_name,
      eligibility: '', // Eligibility not in new API
      avatarUrl: payer.avatar_url,
    }));
  }, [payerResults]);

  const handleInputChange = useCallback((event: React.ChangeEvent<{}>, newInputValue: string) => {
    setInputValue(newInputValue);
  }, []);

  const renderOption = useCallback(
    (
      props: React.HTMLAttributes<HTMLLIElement>,
      option: { id: string; name: string; avatarUrl?: string | null },
    ) => {
      return (
        <li {...props}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {option.avatarUrl && (
              <Avatar
                src={option.avatarUrl}
                alt={option.name}
                sx={{ width: 24, height: 24 }}
                variant="rounded"
              />
            )}
            <span>
              {option.name} ({option.id})
            </span>
          </Box>
        </li>
      );
    },
    [],
  );

  const getOptionLabel = useCallback(
    (option: { id: string; name: string; eligibility: string }) => {
      return option.name;
    },
    [],
  );

  const isOptionEqualToValue = useCallback(
    (
      option: { id: string; name: string; eligibility: string },
      value: { id: string; name: string; eligibility: string },
    ) => {
      return option.id === value.id;
    },
    [],
  );

  return (
    <Box>
      <Autocomplete
        options={mappedPayers}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        onInputChange={handleInputChange}
        onChange={(_event, newValue) => {
          if (newValue) {
            onPayerSelect({
              id: newValue.id,
              name: newValue.name,
              eligibility: newValue.eligibility,
            });
          } else {
            onPayerSelect(null);
          }
        }}
        value={value}
        isOptionEqualToValue={isOptionEqualToValue}
        filterOptions={x => x}
        renderInput={params => (
          <TextField
            {...params}
            label="Search Payers"
            placeholder="Search by name or payer ID"
            variant="outlined"
            disabled={disabled}
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            helperText={
              inputValue.length > 0 && inputValue.length < 2
                ? 'Type at least 2 characters to search by name or payer ID'
                : 'Search by payer name or ID'
            }
          />
        )}
        loading={isLoading}
        loadingText="Searching..."
        noOptionsText={
          inputValue.length >= 2 ? 'No payers found' : 'Type to search for payers'
        }
      />
    </Box>
  );
};

export default PayerSearch;
